import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { SharedModule } from '../../shared/shared.module';
import { MatIconModule } from '@angular/material/icon';
import { UtilService } from '@app/shared/services/util.service';
import { SettingsService } from '../settings.service';
import { PREFERENCES } from '@app/shared/constants/application-constants';
import { forkJoin } from 'rxjs';
export enum constants {
  'IN_APP_PUSH' = 'in_app_push',
  'IN_APP' = 'inAppPush',
}

export interface Preference {
  id: string;
  completed: boolean;
  subtasks?: Preference[];
}

export interface Service {
  service: string;
  preferences: Preference[];
}

const MODULE_ORDER = [
  'Leads',
  'Roles',
  'User Category',
  'Tags',
  'Subscription Plans',
  'Subscription Plan',
  'Licence',
];

const MODULE_LABEL_OVERRIDES: Record<string, string> = {
  'subscription_plan': 'Subscription Plan',
  'subscription-plan': 'Subscription Plan',
  'subscription.plan': 'Subscription Plan',
  'subscription plan': 'Subscription Plan',
  'subscription plans': 'Subscription Plans',
  'license': 'Licence',
  'licence': 'Licence',
  'license module': 'Licence',
  'licence module': 'Licence',
};

@Component({
    imports: [FormsModule, SharedModule, MatIconModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-notificationsettings',
    templateUrl: './notificationsettings.component.html',
    styleUrls: ['./notificationsettings.component.css']
})
export class NotificationsettingsComponent implements OnInit {
  servicesList = [];
  readonly constants : typeof constants = constants ;  
  preferences = PREFERENCES;
  preferencesList: any[] = [];
  allComplete: any = {};
  settings: any = [];
  serviceError: any
  tags: any = {};

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private util: UtilService,
    private settingsService: SettingsService
  ) {}

  isLoading: boolean = true;

  private isSystemSuperAdmin(): boolean {
    return localStorage.getItem('isSystemSuperAdmin') === 'true';
  }

  private isSubscriptionPlanModule(moduleName: string): boolean {
    const normalized = (moduleName || '').trim().toLowerCase();
    return (
      normalized.includes('subscription plan') ||
      normalized.includes('subscription plans') ||
      normalized.includes('subscription_plan') ||
      normalized.includes('subscription-plan')
    );
  }

  private shouldHideModule(item: any): boolean {
    if (this.isSystemSuperAdmin()) {
      return false;
    }

    const moduleLabel = this.getModuleLabel(item);
    const eventName = (item?.event || '').trim().toLowerCase();
    const menuName = this.normalizeModuleName(item?.menu_name);

    return (
      this.isSubscriptionPlanModule(moduleLabel) ||
      this.isSubscriptionPlanModule(menuName) ||
      eventName.startsWith('subscription_plan') ||
      eventName.startsWith('subscription.plan') ||
      eventName.startsWith('subscription-plan')
    );
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.preferencesList = [];
    this.allComplete = {};
    this.settings = [];
    this.servicesList = [];

    let resp = this.settingsService.getAllNotificationEvents();
    resp.subscribe(
      (data: any) => {
        let obj: any = {};
        const response = data.body.responseBody;
  
        const filteredResponse = Object.keys(response).reduce((accumulator: any, key) => {
          accumulator[key] = (response[key] || []).filter((item: any) => !this.shouldHideModule(item));
          return accumulator;
        }, {});

        const services = Object.keys(response).map((key) => {
          return (filteredResponse[key] || []).map((item: any) => this.getModuleLabel(item));
        });
        this.servicesList = [...new Set(services.flat(1))].sort((a, b) => this.sortModules(a, b));
  
        Object.keys(filteredResponse).forEach((key) => {
          filteredResponse[key].forEach((item: any) => {
            const serviceName = this.getModuleLabel(item);

            if (obj?.[serviceName]) {
              this.preferences.forEach((preference) => {
                const index = obj[serviceName].preferences.findIndex(
                  (item: any) => item.id === preference
                );
                obj[serviceName].preferences[index].subtasks.push({
                  id: item.event_id,
                  description: item.description,
                  completed: item.event === 'content.download_question_paper' ? true : false,
                });
              });
            } else {
              obj[serviceName] = {
                service: serviceName,
                preferences: [],
              };
              this.preferences.forEach((preference) => {
                obj[serviceName].preferences.push({
                  id: preference,
                  completed: false,
                  subtasks: [
                    {
                      id: item.event_id,
                      description: item.description,
                      completed: item.event === 'content.download_question_paper' ? true : false,
                    },
                  ],
                });
              });
            }
          });
        });
  
        Object.keys(obj)
          .sort((a, b) => this.sortModules(a, b))
          .forEach((item) => {
            this.preferencesList.push(obj[item]);
        });
  
        this.servicesList.forEach((service) => {
          let preferenceObj = {};
          this.preferences.forEach((preference) => {
            preferenceObj[preference] = false;
          });
          this.allComplete[service] = preferenceObj;
        });
  
        // Now fetch notification settings
        this.settingsService.getNotificationSettings().subscribe(
          (data: any) => {
            const response = data.body.responseBody;
  
            if (response?.length) {
              this.settings = response.filter((item: any) => !this.shouldHideModule(item));

              // Step 1: Apply all saved checkbox states from server
              response?.forEach((item: any) => {
                if (this.shouldHideModule(item)) {
                  return;
                }
                this.preferencesList.forEach((preferenceGroup, index) => {
                  preferenceGroup.preferences.forEach((pref: any, pIndex: number) => {
                    pref.subtasks.forEach((task: any, tIndex: number) => {
                      if (task.id == item.event_id) {
                        const isChecked = item[pref.id];
                        this.preferencesList[index].preferences[pIndex].subtasks[tIndex].completed = isChecked;
                      }
                    });
                  });
                });
              });

              // Step 2: Recompute allComplete parent state AFTER all subtasks are updated
              this.preferencesList.forEach((preferenceGroup, index) => {
                preferenceGroup.preferences.forEach((pref: any, pIndex: number) => {
                  const subtasks = this.preferencesList[index].preferences[pIndex].subtasks;
                  if (subtasks && subtasks.length > 0) {
                    const allSubtasksChecked = subtasks.every((st: any) => st.completed);
                    this.allComplete[preferenceGroup.service][pref.id] = allSubtasksChecked;
                  }
                });
              });
            }
  
            this.isLoading = false;
            this.changeDetectorRef.markForCheck();
            this.changeDetectorRef.detectChanges();
          },
        (err: any) => {
            this.isLoading = false;
            this.serviceError = this.util.getIntlErrorMessage(
              "NOTIFICATION_SETTING_UPDATE",
              err.responseHead.statusCode,
              err.responseHead.statusDesc
            );
            console.error("Error fetching notification settings:", err);
            this.util.simpleDialog({
              title: 'Error',
              message: this.serviceError || err
            });
          }
        );
  
        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
      },
      (err: any) => {
        this.isLoading = false;
        this.serviceError = this.util.getIntlErrorMessage(
          "NOTIFICATION_SETTING_UPDATE",
          err.responseHead.statusCode,
          err.responseHead.statusDesc
        );
        console.error("Error fetching notification events:", err);
        this.util.simpleDialog({
          title: 'Error',
          message: this.serviceError || err
        });
      }
    );
  }

  private normalizeModuleName(moduleName: string): string {
    const trimmed = (moduleName || '').trim();
    if (!trimmed) {
      return 'Unknown Module';
    }

    const override = MODULE_LABEL_OVERRIDES[trimmed.toLowerCase()];
    if (override) {
      return override;
    }

    return trimmed
      .replace(/[_\-.]+/g, ' ')
      .replace(/\s+/g, ' ')
      .split(' ')
      .map((part) => part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : part)
      .join(' ');
  }

  private getModuleLabel(item: any): string {
    const moduleName = this.normalizeModuleName(item?.menu_name);
    const eventName = (item?.event || '').trim().toLowerCase();

    if (eventName.startsWith('license') || eventName.startsWith('licence')) {
      return 'Licence';
    }

    if (
      eventName.startsWith('subscription_plan') ||
      eventName.startsWith('subscription.plan') ||
      eventName.startsWith('subscription-plan')
    ) {
      return 'Subscription Plan';
    }

    if (moduleName !== 'Unknown Module') {
      if (eventName === 'app.master.tags_master') {
        return 'Tags';
      }
      return moduleName;
    }

    if (eventName === 'app.master.tags_master') {
      return 'Tags';
    }
    if (eventName.startsWith('subscription_plan')) {
      return 'Subscription Plan';
    }

    return moduleName;
  }

  private sortModules(a: string, b: string): number {
    const indexA = MODULE_ORDER.indexOf(a);
    const indexB = MODULE_ORDER.indexOf(b);

    if (indexA !== -1 || indexB !== -1) {
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    }

    return a.localeCompare(b);
  }

  private resolveEventId(item: any, event?: any): number | string | null {
    return (
      item?.id ??
      item?.event_id ??
      item?.eventId ??
      event?.id ??
      event?.event_id ??
      event?.eventId ??
      null
    );
  }

  getRequestBody(id: string, item: any, event: any) {
    const eventId = this.resolveEventId(item, event);
    if (eventId === null || eventId === undefined || eventId === '') {
      throw new Error('Unable to resolve eventId for notification settings request');
    }

    let obj: any = { eventId };
    this.preferences.forEach((preference) => {
      if (id === constants.IN_APP_PUSH && preference === id) {
        obj[constants.IN_APP] = item.completed;
      } else if (id === preference) {
        obj[id] = item.completed;
      } else {
        if (preference === constants.IN_APP_PUSH) {
          obj[constants.IN_APP] = event? event.in_app_push : false;
        } else {
          obj[preference] = event? event[preference] : false;
        }
      }
    });

    return obj;
  }

  updateAllComplete(id: string, item: any) {
    //("updateAllComplete called with id:", id, "item:", item);
    this.isLoading = true; // Start loading
    //("isLoading set to true");

    const event = this.settings.find(
      (setting: any) =>
        setting.event_id === item.id ||
        setting.id === item.id ||
        setting.eventId === item.id
    );
    //("Found event:", event);
    /// console.log(event,'event');
    let data: any;
    try {
      data = this.getRequestBody(id, item, event);
    } catch (error) {
      console.error(error);
      this.serviceError =
        'Unable to save notification setting because the event id could not be determined.';
      this.util.simpleDialog({
        title: 'Error',
        message: this.serviceError,
      });
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
      this.changeDetectorRef.markForCheck();
      return;
    }

    //("Request body generated:", data);

    if (event) {
        //("Updating existing notification settings...");
        this.settingsService.updateNotificationSettings(event.id, data).subscribe(
            (data: any) => {
                //("Update API success response:", data);
                const response = data.body.responseBody;
                const responseHead = data.body.responseHead;
                const index = this.settings.findIndex((item: any) => item.id == response.id);
                //("Index of updated setting:", index);
                
                if (index !== -1) {
                    this.settings.splice(index, 1);
                }
                this.settings.push(response);
                //("Added updated setting:", response);

                const successMsg = responseHead 
                    ? this.util.getSuccessMessage('NOTIFICATION_SETTING_UPDATE', responseHead.statusCode, responseHead.statusDesc) 
                    : responseHead;
                //("Success message:", successMsg);
                this.util.simpleDialog({
                    title: 'Success',
                    message: successMsg,
                });

                this.isLoading = false; // Stop loading after success
                this.changeDetectorRef.detectChanges();
                this.changeDetectorRef.markForCheck();
                //("isLoading set to false (success)");
            }, 
            (err: any) => {
                console.error("Update API error response:", err);
                if (err.responseHead) {
                    this.serviceError = this.util.getIntlErrorMessage("NOTIFICATION_SETTING_UPDATE", err.responseHead.statusCode, err.responseHead.statusDesc);
                    //("Error message:", this.serviceError);
                    this.util.simpleDialog({
                        title: 'Error',
                        message: this.serviceError
                    });
                }
                this.isLoading = false; // Stop loading after error
                //("isLoading set to false (error)");
            }
        );
    } else {
        //("Saving new notification settings...");
        this.settingsService.saveNotificationSettings(data).subscribe(
            (data: any) => {
                //("Save API success response:", data);
                this.isLoading = false; // Stop loading early in case of success

                const response = data.body.responseBody;
                const responseHead = data.body.responseHead;
                const index = this.settings.findIndex((item: any) => item.id == response.id);
                //("Index of new setting:", index);

                if (index !== -1) {
                    this.settings.splice(index, 1);
                }
                this.settings.push(response);
                //("Added new setting:", response);

                const successMsg = responseHead 
                    ? this.util.getSuccessMessage('NOTIFICATION_SETTING_UPDATE', responseHead.statusCode, responseHead.statusDesc) 
                    : responseHead;
                //("Success message:", successMsg);
                this.util.simpleDialog({
                    title: 'Success',
                    message: successMsg,
                });

                this.isLoading = false; // Stop loading after success
                this.changeDetectorRef.detectChanges();
                this.changeDetectorRef.markForCheck();
            }, 
            (err: any) => {
                console.error("Save API error response:", err);
                if (err.responseHead) {
                    this.serviceError = this.util.getIntlErrorMessage("NOTIFICATION_SETTING_UPDATE", err.responseHead.statusCode, err.responseHead.statusDesc);
                    //("Error message:", this.serviceError);
                    this.util.simpleDialog({
                        title: 'Error',
                        message: this.serviceError
                    });
                }
                this.isLoading = false; // Stop loading after error
                //("isLoading set to false (error)");
            }
        );
    }
}




someComplete(service: string, id: string) {
  const index = this.preferencesList.findIndex(
    (item) => item.service == service
  );
  if (index === -1) return false;

  const subIndex = this.preferencesList[index].preferences.findIndex(
    (item: any) => item.id == id
  );
  if (subIndex === -1) return false;

  const subtasks = this.preferencesList[index].preferences[subIndex].subtasks;

  if (!subtasks || subtasks.length === 0) {
    return false;
  }

  const completedCount = subtasks.filter((t: any) => t.completed).length;

  // ✅ Returns true only if some are completed but not all
  return completedCount > 0 && completedCount < subtasks.length;
}



  setAll(completed: boolean, service: string, subitems: any) {

  
    const index = this.preferencesList.findIndex((item) => item.service === service);

  
    if (index === -1) {
      console.warn('❌ Service not found in preferencesList:', service);
      return;
    }
  
    const subIndex = this.preferencesList[index].preferences.findIndex(
      (item: any) => item.id === subitems.id
    );
  
    if (subIndex === -1) {
      console.warn('❌ SubItem not found in preferences:', subitems.id);
      return;
    }
  
    const subtasks = this.preferencesList[index].preferences[subIndex].subtasks;
    if (!subtasks) {
      console.warn('⚠️ No subtasks found for:', subitems.id);
      return;
    }
  

    subtasks.forEach((t: any, i: number) => {

      t.completed = completed;

    });
  
    // Sync parent checkbox state
    this.allComplete[service][subitems.id] = completed;

  
    this.changeDetectorRef.markForCheck();

    this.changeDetectorRef.detectChanges();

    // Create observables to save or update each subtask
    const observables = [];
    for (const task of subitems.subtasks) {
      const event = this.settings.find(
        (setting: any) =>
          setting.event_id === task.id ||
          setting.id === task.id ||
          setting.eventId === task.id
      );

      let data: any;
      try {
        data = this.getRequestBody(subitems.id, task, event);
      } catch (error) {
        console.error(error);
        this.serviceError =
          'Unable to save notification setting because the event id could not be determined.';
        this.util.simpleDialog({
          title: 'Error',
          message: this.serviceError,
        });
        this.isLoading = false;
        this.changeDetectorRef.detectChanges();
        this.changeDetectorRef.markForCheck();
        return;
      }

      if (event) {
        observables.push(this.settingsService.updateNotificationSettings(event.id, data));
      } else {
        observables.push(this.settingsService.saveNotificationSettings(data));
      }
    }
  
    this.isLoading = true;

    this.changeDetectorRef.detectChanges();

    forkJoin(observables).subscribe({
      next: (responses) => {

  
        responses.forEach((data: any, i: number) => {
          const response = data.body.responseBody;
          const index = this.settings.findIndex((item: any) => item.id == response.id);
  
          if (index !== -1) {

            this.settings.splice(index, 1);
          } else {
            console.log(`➕ Adding new setting at index ${i}`);
          }
  
          this.settings.push(response);
        });

        this.util.simpleDialog({
          title: 'Success',
          message: 'Notification settings updated successfully.',
        });
  
        this.isLoading = false;
  
        this.changeDetectorRef.detectChanges();
        this.changeDetectorRef.markForCheck();
      },
      error: (err) => {
        console.error('❌ Error in bulk update:', err);
        this.serviceError = this.util.getIntlErrorMessage(
          "NOTIFICATION_SETTING_UPDATE",
          err.responseHead?.statusCode,
          err.responseHead?.statusDesc
        );
        this.util.simpleDialog({
          title: 'Error',
          message: this.serviceError || 'Something went wrong while updating settings.'
        });
        this.isLoading = false;
        console.log('❌ Done - isLoading set to false (error)');
      },
    });
  }
  
  
  
  
}
